import { FormattedMessage } from 'react-intl'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Divider from 'LuxKit/Divider'
import FormatCurrency from 'components/Common/FormatCurrency'
import {
  FREE_DISTANCE_KM,
  EXTRA_DISTANCE_PRICE_PER_KM,
  CAR_SEAT_FEE,
  VEHICLE_UPGRADE_FEE,
} from 'checkout/hooks/loyalty/useTransferPricing'

interface Props {
  kmFromAirport: number
  hasExtraDistanceCharge: boolean
  extraDistanceCharge: number
  hasCarSeatCharge: boolean
  needsVehicleUpgrade: boolean
  totalExtraCharge: number
}

function TransferSurchargeSummary(props: Props) {
  const {
    kmFromAirport,
    hasExtraDistanceCharge,
    extraDistanceCharge,
    hasCarSeatCharge,
    needsVehicleUpgrade,
    totalExtraCharge,
  } = props

  return (
    <>
      <Divider kind="primary" />
      <VerticalSpacer gap={4}>
        <BodyText variant="large" weight="bold">
          <FormattedMessage
            id="lOgz0OK95yoFRqB3NIfC"
            defaultMessage="Your airport drop off transfer"
            description="Heading for the airport drop-off transfer surcharge summary section in checkout"
          />
        </BodyText>
        <Heading variant="heading5" colour="normal-contrast">
          <FormattedMessage
            id="t_Zwx8Em0_Od4Pn06haG"
            defaultMessage="{price} extra"
            description="Extra charge amount for airport drop-off transfer surcharge in checkout loyalty benefits section"
            values={{ price: <FormatCurrency value={totalExtraCharge} currency="AUD" /> }}
          />
        </Heading>
        <BodyText variant="medium" weight="bold">
          <FormattedMessage
            id="tsyzgUfwIkooqmzVHMqd"
            defaultMessage="The transfers company will contact you to arrange payment."
            description="Notice that the transfer company will contact the user to arrange surcharge payment"
          />
        </BodyText>
        <VerticalSpacer gap={2} as="ul">
          {hasExtraDistanceCharge && (
            <BodyText variant="small" as="li">
              <FormattedMessage
                id="2cSKfknYc54x19jwyUAT"
                defaultMessage="Long distance fee: {extraCharge}. Your address is {distance}km from the airport. {freeDistance}km are included for free with {pricePerKm} per extra km"
                description="Breakdown of the long distance surcharge fee for airport transfer exceeding free distance"
                values={{
                  distance: kmFromAirport,
                  freeDistance: FREE_DISTANCE_KM,
                  extraCharge: <FormatCurrency value={extraDistanceCharge} currency="AUD" />,
                  pricePerKm: <FormatCurrency value={EXTRA_DISTANCE_PRICE_PER_KM} currency="AUD" />,
                }}
              />
            </BodyText>
          )}
          {hasCarSeatCharge && (
            <BodyText variant="small" as="li">
              <FormattedMessage
                id="FL9pjHN8ry7gKgFZ3T3U"
                defaultMessage="Car seat additional fee: {fee}."
                description="Line item showing the additional car seat fee in the transfer surcharge summary"
                values={{ fee: <FormatCurrency value={CAR_SEAT_FEE} currency="AUD" /> }}
              />
            </BodyText>
          )}
          {needsVehicleUpgrade && (
            <BodyText variant="small" as="li">
              <FormattedMessage
                id="TPyfOC18sUkpLX3y1Fi-"
                defaultMessage="Vehicle upgrade fee: {fee}. Your number of passengers and bags exceeds the capacity of the standard sedan. A vehicle upgrade to a van is required"
                description="Explanation of the vehicle upgrade fee when passengers and bags exceed sedan capacity"
                values={{ fee: <FormatCurrency value={VEHICLE_UPGRADE_FEE} currency="AUD" /> }}
              />
            </BodyText>
          )}
        </VerticalSpacer>
      </VerticalSpacer>
    </>
  )
}

export default TransferSurchargeSummary
